import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import { Auth0Provider } from "@auth0/auth0-react";
import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import posthog from 'posthog-js';
import { PostHogProvider } from "posthog-js/react";

// Create a client
const queryClient = new QueryClient();

if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN !== 'http://localhost' ) {
  posthog.init(window.__RUNTIME_CONFIG__.REACT_APP_PUBLIC_POSTHOG_KEY || '', {
    api_host: window.__RUNTIME_CONFIG__.REACT_APP_PUBLIC_POSTHOG_HOST,
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // Highly recommended as a minimum!!
        color: false,
        date: false,
        'datetime-local': false,
        email: false,
        month: false,
        number: false,
        range: false,
        search: false,
        tel: false,
        text: false,
        time: false,
        url: false,
        week: false,
        textarea: false,
        select: false,
      }
    },
    loaded: (posthog) => {
      // if (process.env.NODE_ENV != 'development') 
      posthog.startSessionRecording()
    }
  })
}


window.addEventListener('vite:preloadError', () => {
  const hasReloaded = sessionStorage.getItem('vite_preload_error_reloaded');
  if (!hasReloaded) {
    sessionStorage.setItem('vite_preload_error_reloaded', 'true');
    window.location.reload();
  }
});

const container = document.getElementById("root");
const root = createRoot(container!);
const audience = window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_AUDIENCE || "";

// Handle redirect after login, navigating to the original URL
// Can be disabled via REACT_APP_DISABLE_RETURN_TO_URL env variable for rollback
const onRedirectCallback = (appState?: any) => {
  const disableReturnToUrl = window.__RUNTIME_CONFIG__.REACT_APP_DISABLE_RETURN_TO_URL === 'true';
  
  if (disableReturnToUrl) {
    // Old behavior: always go to root
    window.history.replaceState({}, document.title, '/');
  } else {
    // New behavior: return to intended URL
    window.history.replaceState(
      {},
      document.title,
      appState?.returnTo || window.location.pathname
    );
  }
};

root.render(
  <React.StrictMode>
    <Auth0Provider
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      domain={
        window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_ISSUER_BASE_URL
          ? window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_ISSUER_BASE_URL
          : ""
      }
      clientId={
        window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_CLIENT_ID
          ? window.__RUNTIME_CONFIG__.REACT_APP_AUTH0_CLIENT_ID
          : ""
      }
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation={"localstorage"}
      skipRedirectCallback={window.location.pathname === "/newIntegration"}
    >
      <QueryClientProvider client={queryClient}>
        <MantineProvider withGlobalStyles withNormalizeCSS theme={{
          fontFamily: 'Inter', 
          primaryColor: 'indigo',
        }}>
          <PostHogProvider client={posthog}>
            <Notifications zIndex={9999} />
            <App />
          </PostHogProvider>
        </MantineProvider>
      </QueryClientProvider>
    </Auth0Provider>
  </React.StrictMode>
);
